import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiClient } from '../../services/api';
import { OrganizationQuotaDetails } from '../../types/domain';
import { useOrganizationBilling } from './hooks/useOrganizationBilling';
import { useOrganizationTeam } from './hooks/useOrganizationTeam';

export const useOrganizationData = (activeTab: string, settingsTab: string) => {
  const navigate = useNavigate();
  const {
    user,
    currentOrg,
    projects,
    createProject,
    refreshOrganizations,
    role,
  } = useAuth();
  const { showToast } = useToast();

  const canManageOrg = Boolean(user?.is_superadmin || role === 'owner' || role === 'admin');
  const canDeleteOrg = Boolean(user?.is_superadmin || role === 'owner');

  // Quotas State
  const [quota, setQuota] = useState<OrganizationQuotaDetails | null>(null);
  const [isLoadingQuota, setIsLoadingQuota] = useState(false);

  // New Project Modal State
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  // Org Settings State
  const [orgName, setOrgName] = useState(currentOrg?.name || '');
  const [isSavingOrg, setIsSavingOrg] = useState(false);
  const [copiedOrgId, setCopiedOrgId] = useState(false);

  // Delete Org State
  const [deleteOrgModalOpen, setDeleteOrgModalOpen] = useState(false);
  const [deleteOrgConfirmText, setDeleteOrgConfirmText] = useState('');
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);

  const fetchOrgQuota = useCallback(async () => {
    if (!currentOrg?.id) return;
    setIsLoadingQuota(true);
    try {
      const q = await apiClient<OrganizationQuotaDetails>(`/organizations/${currentOrg.id}/quota`);
      setQuota(q);
    } catch (err) {
      console.warn('Failed to load organization quota:', err);
    } finally {
      setIsLoadingQuota(false);
    }
  }, [currentOrg?.id]);

  // Compose Billing hook
  const billing = useOrganizationBilling(fetchOrgQuota);

  // Compose Team hook
  const team = useOrganizationTeam();

  useEffect(() => {
    if (currentOrg) {
      setOrgName(currentOrg.name);
      fetchOrgQuota();
    }
  }, [currentOrg, fetchOrgQuota]);

  useEffect(() => {
    if (activeTab === 'team') {
      team.fetchTeamMembers();
    }
  }, [activeTab, currentOrg?.id, team.fetchTeamMembers]);

  useEffect(() => {
    if (activeTab === 'settings' && settingsTab === 'billing') {
      billing.fetchPlans();
    }
  }, [activeTab, settingsTab, billing.fetchPlans]);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectName.trim() || !currentOrg) return;

    setIsCreatingProject(true);
    try {
      const proj = await createProject(currentOrg.id, newProjectName.trim());
      showToast({
        type: 'success',
        title: 'Project Created',
        message: `Project "${proj.name}" created under ${currentOrg.name}.`,
      });
      setNewProjectName('');
      setCreateProjectOpen(false);
      navigate(`/organization/${currentOrg.id}/projects/${proj.id}`);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Create Project',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleSaveOrgSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgName.trim() || !currentOrg?.id) return;

    setIsSavingOrg(true);
    try {
      await apiClient(`/organizations/${currentOrg.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: orgName.trim() }),
      });
      showToast({
        type: 'success',
        title: 'Organization Updated',
        message: 'Organization details have been saved.',
      });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Update Organization',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsSavingOrg(false);
    }
  };

  const copyOrgId = () => {
    if (currentOrg?.id) {
      navigator.clipboard.writeText(currentOrg.id);
      setCopiedOrgId(true);
      setTimeout(() => setCopiedOrgId(false), 2000);
      showToast({
        type: 'info',
        title: 'Copied',
        message: 'Organization ID copied to clipboard.',
      });
    }
  };

  const handleDeleteOrg = async () => {
    if (!currentOrg?.id || deleteOrgConfirmText !== currentOrg.slug) return;
    setIsDeletingOrg(true);
    try {
      await apiClient(`/organizations/${currentOrg.id}`, {
        method: 'DELETE',
      });
      showToast({
        type: 'success',
        title: 'Organization Deleted',
        message: `Organization "${currentOrg.name}" and its storage files were permanently removed.`,
      });
      setDeleteOrgModalOpen(false);
      await refreshOrganizations();
      navigate('/dashboard');
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Deletion Failed',
        message: err?.message || 'Could not delete organization',
      });
    } finally {
      setIsDeletingOrg(false);
    }
  };

  return {
    user,
    currentOrg,
    projects,
    canManageOrg,
    canDeleteOrg,
    quota,
    isLoadingQuota,
    fetchOrgQuota,
    createProjectOpen,
    setCreateProjectOpen,
    newProjectName,
    setNewProjectName,
    isCreatingProject,
    handleCreateProject,
    orgName,
    setOrgName,
    isSavingOrg,
    handleSaveOrgSettings,
    copiedOrgId,
    copyOrgId,
    deleteOrgModalOpen,
    setDeleteOrgModalOpen,
    deleteOrgConfirmText,
    setDeleteOrgConfirmText,
    isDeletingOrg,
    handleDeleteOrg,
    // Team
    members: team.members,
    isLoadingMembers: team.isLoadingMembers,
    fetchTeamMembers: team.fetchTeamMembers,
    invitations: team.invitations,
    inviteModalOpen: team.inviteModalOpen,
    setInviteModalOpen: team.setInviteModalOpen,
    inviteEmail: team.inviteEmail,
    setInviteEmail: team.setInviteEmail,
    inviteRole: team.inviteRole,
    setInviteRole: team.setInviteRole,
    inviteProjects: team.inviteProjects,
    setInviteProjects: team.setInviteProjects,
    isSendingInvite: team.isSendingInvite,
    generatedInviteUrl: team.generatedInviteUrl,
    setGeneratedInviteUrl: team.setGeneratedInviteUrl,
    copiedLink: team.copiedLink,
    handleSendInvite: team.handleSendInvite,
    handleCopyInviteLink: team.handleCopyInviteLink,
    isRevokingInvite: team.isRevokingInvite,
    handleRevokeInvitation: team.handleRevokeInvitation,
    managingMember: team.managingMember,
    setManagingMember: team.setManagingMember,
    memberAssignedProjects: team.memberAssignedProjects,
    setMemberAssignedProjects: team.setMemberAssignedProjects,
    isLoadingMemberProjects: team.isLoadingMemberProjects,
    isSavingMemberProjects: team.isSavingMemberProjects,
    openManageProjects: team.openManageProjects,
    handleSaveMemberProjects: team.handleSaveMemberProjects,
    memberToRemove: team.memberToRemove,
    setMemberToRemove: team.setMemberToRemove,
    isRemovingMember: team.isRemovingMember,
    handleExecuteRemoveMember: team.handleExecuteRemoveMember,
    // Billing
    plans: billing.plans,
    isLoadingPlans: billing.isLoadingPlans,
    fetchPlans: billing.fetchPlans,
    isMutatingSubscription: billing.isMutatingSubscription,
    upgradeTargetPlan: billing.upgradeTargetPlan,
    setUpgradeTargetPlan: billing.setUpgradeTargetPlan,
    downgradeTargetPlan: billing.downgradeTargetPlan,
    setDowngradeTargetPlan: billing.setDowngradeTargetPlan,
    cancelModalOpen: billing.cancelModalOpen,
    setCancelModalOpen: billing.setCancelModalOpen,
    handleUpgrade: billing.handleUpgrade,
    handleDowngrade: billing.handleDowngrade,
    handleCancelSubscription: billing.handleCancelSubscription,
    handleResumeSubscription: billing.handleResumeSubscription,
  };
};
